/**
 * Rasterize a DOM element to a multi-page A4 PDF.
 *
 * Pipeline:
 *   1. html-to-image's `toSvg` serializes the live DOM into an SVG <foreignObject>,
 *      which captures modern CSS color functions (oklch/lab/color-mix) and complex
 *      chart SVGs without manual sanitization.
 *   2. Rasterize that SVG one A4 page at a time, avoiding splits inside no-break
 *      elements (tables, images, figures, code blocks) and biasing page breaks
 *      toward mostly-white horizontal gaps so glyphs aren't cut in half. One canvas
 *      can't hold a long document: html-to-image's `toCanvas` shrinks it to 16,384px
 *      tall, blurring the text.
 *   3. Add each page to a jsPDF document and save.
 *
 * Trade-off: the PDF is a flat image, not text-searchable. We accept that to keep
 * the export entirely client-side and one-click.
 */

const PAGE_WIDTH_PT = 595.28;
const PAGE_HEIGHT_PT = 841.89;
const DEFAULT_MARGIN_X_PT = 14;
const DEFAULT_MARGIN_Y_PT = 28;
const PIXEL_RATIO = 2;
const EXPORT_CLASS = 'pdf-exporting';
// A PDF is paper, so the captured subtree is re-themed light whatever the app's theme is. `el` must
// not be the element carrying `dark` itself: both rules would match one element and dark would win.
const FORCE_LIGHT_CLASS = 'fp-light';
const DEFAULT_NO_BREAK_SELECTORS = 'table, img, svg, figure, pre';
const HEADING_SELECTOR = 'h1, h2, h3, h4, h5, h6';

// Re-theming the subtree (see FORCE_LIGHT_CLASS) starts every transition that watches a colour, and
// the capture runs inside them, so a transitioned property is photographed at its pre-capture value.
const FREEZE_ANIMATION_CSS = `
  .${EXPORT_CLASS}, .${EXPORT_CLASS} * {
    transition: none !important;
    animation: none !important;
  }
`;

// Mirrors the loader html-to-image's `toCanvas` uses before drawing the SVG.
const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      image.decode().then(() => requestAnimationFrame(() => resolve(image)), reject);
    };
    image.onerror = () => reject(new Error('Could not load the captured element.'));
    image.decoding = 'async';
    image.src = src;
  });

// Tables scroll inside an `overflow: auto` wrapper on screen; a page has no scrollbar, so the
// wrapper has to expand and long cell content has to reflow instead of overflowing. `max-width`
// plus the cell wrapping is what makes a wide table fit; leave `table-layout` alone, or columns
// stop being sized to their content and every capture's tables shift.
export const PDF_TABLE_OVERRIDES_CSS = `
  .${EXPORT_CLASS} [class*="tableWrapper"] {
    overflow: visible !important;
    max-width: 100% !important;
    width: 100% !important;
  }
  .${EXPORT_CLASS} table {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
  }
  .${EXPORT_CLASS} table td,
  .${EXPORT_CLASS} table th {
    word-break: break-word !important;
    overflow-wrap: anywhere !important;
    white-space: normal !important;
  }
`;

export type DomToPdfOptions = {
  filename: string;
  // CSS to inject for the duration of the capture, scoped under `.pdf-exporting`.
  // Use this to force overflow-auto wrappers to expand, etc.
  exportOverridesCss?: string;
  // Selectors for elements that must not be split across pages.
  noBreakSelectors?: string;
  // Resolves once the element is ready to capture. Throws on timeout. Default: returns immediately.
  waitForReady?: (el: HTMLElement) => Promise<void>;
  minCaptureWidthPx?: number;
  marginXPt?: number;
  marginYPt?: number;
};

export const captureDomToPdf = async (el: HTMLElement, options: DomToPdfOptions): Promise<void> => {
  const marginX = options.marginXPt ?? DEFAULT_MARGIN_X_PT;
  const marginY = options.marginYPt ?? DEFAULT_MARGIN_Y_PT;
  const contentWidthPt = PAGE_WIDTH_PT - marginX * 2;
  const contentHeightPt = PAGE_HEIGHT_PT - marginY * 2;

  if (options.waitForReady) await options.waitForReady(el);

  const [{ toSvg }, { default: JsPDF }] = await Promise.all([import('html-to-image'), import('jspdf')]);

  const styleOverride = document.createElement('style');
  styleOverride.textContent = `${FREEZE_ANIMATION_CSS}${options.exportOverridesCss ?? ''}`;
  document.head.appendChild(styleOverride);
  el.classList.add(EXPORT_CLASS, FORCE_LIGHT_CLASS);
  const prevInlineCss = el.style.cssText;
  el.style.setProperty('color-scheme', 'light');
  if (options.minCaptureWidthPx) {
    el.style.setProperty('min-width', `${options.minCaptureWidthPx}px`, 'important');
  }

  try {
    // Measure no-break elements before capture so we know which canvas-pixel
    // ranges we must not split across pages. We also extend each range upward
    // to include the nearest preceding heading so the title stays with its
    // content. Coordinates are CSS px relative to `el`; scaled to canvas
    // pixels after we know the capture's pixel ratio.
    const noBreakSelectors = options.noBreakSelectors ?? DEFAULT_NO_BREAK_SELECTORS;
    const elTop = el.getBoundingClientRect().top;
    const noBreakRanges = Array.from(el.querySelectorAll(noBreakSelectors)).map(node => {
      const r = node.getBoundingClientRect();
      let topPx = r.top - elTop;
      let cursor: Element | null = node;
      let heading: Element | null = null;
      while (cursor && cursor !== el) {
        let prev = cursor.previousElementSibling;
        while (prev) {
          const h = prev.matches(HEADING_SELECTOR) ? prev : prev.querySelector(HEADING_SELECTOR);
          if (h) {
            heading = h;
            break;
          }
          if (prev.textContent && prev.textContent.trim().length > 0) {
            prev = null;
            break;
          }
          prev = prev.previousElementSibling;
        }
        if (heading || prev) break;
        cursor = cursor.parentElement;
      }
      if (heading) {
        const headingRect = heading.getBoundingClientRect();
        const headingTop = headingRect.top - elTop;
        const headingBottom = headingRect.bottom - elTop;
        // Only attach the heading when it sits within ~120px of the chart/table
        // (a margin's worth of whitespace). Avoids pulling in unrelated headings.
        if (topPx - headingBottom < 120) {
          topPx = Math.min(topPx, headingTop);
        }
      }
      return { topPx, bottomPx: r.bottom - elTop };
    });

    // html-to-image inlines each element's computed style, so the live theme is what the image gets.
    // `backgroundColor` paints only the clone's root, which is why a readable dark-mode export
    // needs the light class above rather than this option.
    const svg = await toSvg(el, {
      backgroundColor: '#ffffff',
      cacheBust: true,
      // Suppress html-to-image's web-font fetch pass — it tries to fetch every
      // <link rel="stylesheet"> and @font-face URL to inline them, which our CSP
      // blocks. The captured <foreignObject> SVG uses the live page's fonts.
      fontEmbedCSS: '',
    });
    const image = await loadImage(svg);

    const sourceWidth = image.naturalWidth * PIXEL_RATIO;
    const sourceHeight = image.naturalHeight * PIXEL_RATIO;
    if (sourceWidth === 0 || sourceHeight === 0) {
      throw new Error('Captured element appears empty.');
    }

    const ptPerPx = contentWidthPt / sourceWidth;
    const pageHeightPx = Math.floor(contentHeightPt / ptPerPx);

    const elHeightCssPx = el.getBoundingClientRect().height || 1;
    const cssToCanvasScale = sourceHeight / elHeightCssPx;
    const noBreakSpans = noBreakRanges
      .map(r => ({
        top: Math.floor(r.topPx * cssToCanvasScale),
        bottom: Math.ceil(r.bottomPx * cssToCanvasScale),
      }))
      .filter(s => s.bottom > s.top);

    const pageCanvas = document.createElement('canvas');
    pageCanvas.width = sourceWidth;
    const pageCtx = pageCanvas.getContext('2d', { willReadFrequently: true });
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = sourceWidth;
    const sliceCtx = sliceCanvas.getContext('2d');
    if (!pageCtx || !sliceCtx) throw new Error('Could not allocate 2d canvas context.');

    const isMostlyWhiteRow = (pixels: Uint8ClampedArray, row: number): boolean => {
      const rowStart = row * sourceWidth * 4;
      const inkThreshold = 240;
      const allowedInk = Math.floor(sourceWidth * 0.02);
      let nonWhite = 0;
      for (let x = 0; x < sourceWidth; x++) {
        const i = rowStart + x * 4;
        if (pixels[i] < inkThreshold || pixels[i + 1] < inkThreshold || pixels[i + 2] < inkThreshold) {
          nonWhite++;
          if (nonWhite > allowedInk) return false;
        }
      }
      return true;
    };

    const containingNoBreak = (y: number): { top: number; bottom: number } | null => {
      for (const span of noBreakSpans) {
        if (y > span.top && y < span.bottom) return span;
      }
      return null;
    };

    const findSafeBreak = (proposed: number, sliceStart: number): number => {
      const containing = containingNoBreak(proposed);
      if (containing && containing.top > sliceStart) {
        return containing.top;
      }
      const minRow = Math.max(sliceStart + 1, proposed - Math.floor(pageHeightPx * 0.12));
      const pixels = pageCtx.getImageData(0, minRow - sliceStart, sourceWidth, proposed - minRow + 1).data;
      for (let y = proposed; y >= minRow; y--) {
        if (containingNoBreak(y)) continue;
        if (isMostlyWhiteRow(pixels, y - minRow)) return y;
      }
      return proposed;
    };

    const doc = new JsPDF({ unit: 'pt', format: 'a4' });

    let yOffset = 0;
    let pageIndex = 0;
    while (yOffset < sourceHeight) {
      const remaining = sourceHeight - yOffset;
      const naiveEnd = yOffset + Math.min(pageHeightPx, remaining);
      const isLastPage = naiveEnd >= sourceHeight;

      // One row more than a page, because `findSafeBreak` also checks the row just below it.
      const renderHeight = Math.min(pageHeightPx + 1, remaining);
      pageCanvas.height = renderHeight;
      pageCtx.fillStyle = '#ffffff';
      pageCtx.fillRect(0, 0, sourceWidth, renderHeight);
      pageCtx.drawImage(
        image,
        0,
        yOffset / PIXEL_RATIO,
        image.naturalWidth,
        renderHeight / PIXEL_RATIO,
        0,
        0,
        sourceWidth,
        renderHeight,
      );

      const sliceEnd = isLastPage ? sourceHeight : findSafeBreak(naiveEnd, yOffset);
      const sliceHeight = Math.max(1, sliceEnd - yOffset);

      sliceCanvas.height = sliceHeight;
      sliceCtx.drawImage(pageCanvas, 0, 0, sourceWidth, sliceHeight, 0, 0, sourceWidth, sliceHeight);

      if (pageIndex > 0) doc.addPage();
      const drawHeightPt = sliceHeight * ptPerPx;
      const imgData = sliceCanvas.toDataURL('image/jpeg', 0.92);
      doc.addImage(imgData, 'JPEG', marginX, marginY, contentWidthPt, drawHeightPt, undefined, 'FAST');

      yOffset += sliceHeight;
      pageIndex += 1;
    }

    doc.save(options.filename);
  } finally {
    el.classList.remove(EXPORT_CLASS, FORCE_LIGHT_CLASS);
    el.style.cssText = prevInlineCss;
    styleOverride.remove();
  }
};

export const sanitizePdfFilename = (name: string): string => {
  const base = name
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '')
    .replace(/\s+/g, '-');
  return `${base || 'export'}.pdf`;
};
